import { useState, useEffect, useCallback, memo } from 'react';
import { Box, TextField, Typography, InputAdornment } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { validateField } from '../../../util/validation';

// Formats a numeric value as EUR currency for display
const formatCurrency = value => {
  if (value === null || value === undefined || value === '') return '—';
  const number = typeof value === 'number' ? value : Number.parseFloat(String(value));
  if (Number.isNaN(number)) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(number);
};

// Parses user input into a normalized number with a dot decimal separator
const parseCurrencyInput = input => {
  if (input === null || input === undefined) return null;
  let raw = String(input).replace(/[^0-9.,-]/g, '');
  const hasComma = raw.includes(',');
  if (hasComma) {
    // Remove thousand separators (.) when comma is used as decimal
    raw = raw.replace(/\./g, '').replace(',', '.');
  } else {
    // If no comma exists, allow only one dot as decimal separator (keep last dot)
    const parts = raw.split('.');
    if (parts.length > 2) {
      const last = parts.pop();
      raw = parts.join('') + '.' + last;
    }
  }
  const parsed = Number.parseFloat(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

const EditablePrice = memo(function EditablePrice({
  value,
  setValue,
  isEditable = true,
  label = null,
  placeholder = null,
  isDialog = false,
  required = false,
  showValidation = false,
  schema = null,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [validationError, setValidationError] = useState(null);

  // keep local state in sync
  useEffect(() => {
    setTempValue(value);
  }, [value]);

  useEffect(() => {
    if (!showValidation || !schema) return;
    const validation = validateField(tempValue, schema);
    setValidationError(validation.isValid ? null : validation.errors[0]);
  }, [tempValue, schema, showValidation]);

  const handleClick = useCallback(
    e => {
      e.stopPropagation();
      if (isEditable) {
        setIsEditing(true);
        setTempValue(value);
      }
    },
    [isEditable, value]
  );

  const commit = useCallback(async () => {
    if (schema) {
      const validation = validateField(tempValue, schema);
      if (!validation.isValid) {
        setValidationError(validation.errors[0]);
        return;
      }
    }

    setIsEditing(false);

    if (tempValue !== value) {
      const numeric = parseCurrencyInput(tempValue);
      const finalValue = schema?.transformValue ? schema.transformValue(numeric) : numeric;
      const result = await setValue(finalValue);
      if (result !== true) {
        setTempValue(value);
      } else {
        setValidationError(null);
      }
    }
  }, [tempValue, value, setValue, schema]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') commit();
      if (e.key === 'Escape') {
        setIsEditing(false);
        setTempValue(value);
        setValidationError(null);
      }
    },
    [commit, value]
  );

  const handleChange = useCallback(
    e => {
      const input = e.target.value;
      // Display with comma as decimal separator during editing
      const display = input.replace(/\./g, ',').replace(/[^0-9,\-]/g, '');
      setTempValue(display);
      if (isDialog) {
        const numeric = parseCurrencyInput(display);
        setValue(numeric);
      }
    },
    [isDialog, setValue]
  );

  if (isDialog || isEditing) {
    const inputValue = (() => {
      if (tempValue === null || tempValue === undefined) return '';
      const str = String(tempValue);
      // Ensure comma is used while editing
      return str.replace(/\./g, ',');
    })();
    return (
      <TextField
        fullWidth
        size='small'
        value={inputValue}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        autoFocus={!isDialog}
        label={label}
        placeholder={placeholder || '0,00'}
        required={required}
        error={showValidation && !!validationError}
        helperText={showValidation ? validationError : undefined}
        InputProps={{
          endAdornment: <InputAdornment position='end'>€</InputAdornment>,
          inputMode: 'decimal',
        }}
        sx={{
          minWidth: schema?.minWidth || '120px',
          '& .MuiFormHelperText-root': { margin: 0, fontSize: '0.75rem' },
        }}
      />
    );
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: isEditable ? 'pointer' : 'default',
        p: 1,
        borderRadius: 1,
        '&:hover': isEditable
          ? {
              bgcolor: 'action.hover',
              '.edit-icon': { opacity: 1 },
            }
          : {},
        position: 'relative',
        width: '100%',
        minHeight: '40px',
      }}
    >
      <Typography sx={{ flex: 1 }}>{formatCurrency(value)}</Typography>
      {isEditable && (
        <EditIcon
          className='edit-icon'
          sx={{
            fontSize: 'small',
            ml: 1,
            color: 'action.active',
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
        />
      )}
    </Box>
  );
});

export default EditablePrice;
